import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { questionBankApi } from "@/api/questionBank";
import type {
    QuestionBank,
    QuestionBankFilters,
    CreateQuestionBankData,
    UpdateQuestionBankData,
} from "@/types/questionBank";

export const useQuestionBank = defineStore("questionBank", () => {
    const questions = ref<QuestionBank[]>([]);
    const currentQuestion = ref<QuestionBank | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const page = ref(1);
    const perPage = ref(10);
    const search = ref("");
    const subjectId = ref("");
    const teacherId = ref("");
    const type = ref("");
    const difficulty = ref("");
    const tags = ref("");
    const isActive = ref("");
    const meta = ref<{
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from?: number;
        to?: number;
    } | null>(null);

    const activeQuestions = computed(() =>
        questions.value.filter((question) => question.is_active),
    );

    const inactiveQuestions = computed(() =>
        questions.value.filter((question) => !question.is_active),
    );

    const questionsByType = computed(() => {
        const grouped: Record<string, QuestionBank[]> = {};
        questions.value.forEach((question) => {
            const typeName =
                question.type_display || question.type || "Unknown Type";
            if (!grouped[typeName]) grouped[typeName] = [];
            grouped[typeName].push(question);
        });
        return grouped;
    });

    const questionsByDifficulty = computed(() => {
        const grouped: Record<string, QuestionBank[]> = {};
        questions.value.forEach((question) => {
            const difficultyName =
                question.difficulty_display ||
                question.difficulty ||
                "Unknown Difficulty";
            if (!grouped[difficultyName]) grouped[difficultyName] = [];
            grouped[difficultyName].push(question);
        });
        return grouped;
    });

    const fetchQuestions = async (filters: QuestionBankFilters = {}) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await questionBankApi.getAll({
                page: page.value,
                per_page: perPage.value,
                search: search.value || undefined,
                subject_id: subjectId.value || undefined,
                teacher_id: teacherId.value || undefined,
                type: type.value || undefined,
                difficulty: difficulty.value || undefined,
                tags: tags.value || undefined,
                is_active:
                    isActive.value !== ""
                        ? isActive.value === "true"
                        : undefined,
                ...filters,
            });

            questions.value = response.data?.items || response.data || [];
            meta.value = response.data?.meta || null;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch questions";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchQuestion = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            currentQuestion.value = await questionBankApi.getById(id);
            return currentQuestion.value;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch question";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const createQuestion = async (data: CreateQuestionBankData) => {
        loading.value = true;
        error.value = null;

        try {
            const newQuestion = await questionBankApi.create(data);
            await fetchQuestions();
            return newQuestion;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to create question";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const updateQuestion = async (id: string, data: UpdateQuestionBankData) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedQuestion = await questionBankApi.update(id, data);
            const index = questions.value.findIndex((q) => q.id === id);
            if (index !== -1) {
                questions.value[index] = updatedQuestion;
            }
            if (currentQuestion.value?.id === id) {
                currentQuestion.value = updatedQuestion;
            }
            return updatedQuestion;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to update question";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const deleteQuestion = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            await questionBankApi.delete(id);
            questions.value = questions.value.filter((q) => q.id !== id);
            if (currentQuestion.value?.id === id) {
                currentQuestion.value = null;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to delete question";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchQuestionsBySubject = async (subjectIdValue: string) => {
        loading.value = true;
        error.value = null;

        try {
            const questionsBySubject =
                await questionBankApi.getBySubject(subjectIdValue);
            return questionsBySubject;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch questions";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchQuestionsByTeacher = async (teacherIdValue: string) => {
        loading.value = true;
        error.value = null;

        try {
            const questionsByTeacher =
                await questionBankApi.getByTeacher(teacherIdValue);
            return questionsByTeacher;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch questions";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const clearError = () => {
        error.value = null;
    };

    const setPage = (newPage: number) => {
        if (!meta.value) return;
        if (newPage < 1 || newPage > meta.value.last_page) return;

        page.value = newPage;
        fetchQuestions();
    };

    const setPerPage = (newPerPage: number) => {
        perPage.value = newPerPage;
        page.value = 1;
        fetchQuestions();
    };

    const resetFilters = () => {
        page.value = 1;
        perPage.value = 10;
        search.value = "";
        subjectId.value = "";
        teacherId.value = "";
        type.value = "";
        difficulty.value = "";
        tags.value = "";
        isActive.value = "";
        fetchQuestions();
    };

    return {
        questions,
        currentQuestion,
        loading,
        error,
        page,
        perPage,
        search,
        subjectId,
        teacherId,
        type,
        difficulty,
        tags,
        isActive,
        meta,

        activeQuestions,
        inactiveQuestions,
        questionsByType,
        questionsByDifficulty,

        fetchQuestions,
        fetchQuestion,
        createQuestion,
        updateQuestion,
        deleteQuestion,
        fetchQuestionsBySubject,
        fetchQuestionsByTeacher,
        clearError,
        setPage,
        setPerPage,
        resetFilters,
    };
});

export const useQuestionBankStore = useQuestionBank;
